/**
 * COMISIÓN — validación del techo POR CANAL contra el nivel superior.
 *
 * SSOT de las reglas que el modal de contratos aplica ANTES de guardar y que el backend vuelve a aplicar
 * al escribir (`validarComisionPorCanalContraPadre`, `backend/src/services/contabilidad/contratos.js`).
 * Vive en shared/ para que el front la importe y los tests del backend la ejecuten LITERAL: mientras el
 * test replicaba estas reglas a mano, podían divergir sin ponerse rojo — y divergieron.
 *
 * El backend sigue siendo la autoridad (fail-closed). Esto es el aviso temprano al operador: su veredicto
 * debe coincidir con el del backend en ambos sentidos, porque un guard que aprueba de más frustra al
 * operador con un 400 tardío, y uno que rechaza de más bloquea un contrato válido.
 *
 * Las DOS reglas, ambas por canal y contra el ancestro aplicable (no el padre inmediato — un nivel que no
 * pactó el canal cobra 0% pero no topea a los de abajo; eso lo resuelve quien arma el techo):
 *
 *   1. TECHO — sólo entre bases IGUALES. Un % sobre `ggr` y uno sobre `recarga_jugada` miden cosas
 *      distintas (margen vs jugado): no hay relación de techo entre ellos, comparar sería peras con
 *      manzanas.
 *   2. ZONA CONTIGUA (SPEC_COMISION_BASES_MIXTAS, invariante 3) — dentro de un canal, `recarga_jugada`
 *      sólo puede formar un bloque contiguo DESDE ABAJO. Un hijo `ggr` bajo un superior
 *      `recarga_jugada` deja una zona salteada y se rechaza. La dirección inversa (hijo
 *      `recarga_jugada` bajo superior `ggr`) es válida: quien genera el jugado cobra sobre el jugado y
 *      la red de arriba cobra del margen.
 */

const BASE_GGR = 'ggr';
const BASE_RECARGA_JUGADA = 'recarga_jugada';

/**
 * @typedef {Object} TerminosDeCanal
 * @property {number} porcentaje
 * @property {string} [base] - 'ggr' | 'recarga_jugada'. Ausente ⇒ 'ggr'.
 */

/**
 * @typedef {Object} TechoDeCanal
 * @property {boolean} aplica - false ⇒ ningún ancestro pactó el canal: no hay techo que comparar.
 * @property {number} [porcentaje]
 * @property {string} [base]
 */

/**
 * Primera violación del hijo contra el techo del nivel superior, o null si todo es válido.
 *
 * @param {Object<string, TerminosDeCanal>|null|undefined} porCanalDelHijo - lo que el hijo quiere pactar.
 * @param {Object<string, TechoDeCanal>|null|undefined} techoPorCanal - techo del ancestro aplicable a
 *   cada canal, tal como lo devuelve `GET /api/admin/contabilidad/padre` en `techo_por_canal_padre`.
 * @returns {{ motivo: 'supera_techo'|'zona_salteada', canal: string, pctHijo: number,
 *   techoSuperior: number, baseHijo: string, baseSuperior: string }|null}
 */
export function detectarViolacionDeTechoPorCanal(porCanalDelHijo, techoPorCanal) {
    if (!porCanalDelHijo || !techoPorCanal) return null;

    for (const canal of Object.keys(porCanalDelHijo)) {
        const superior = techoPorCanal[canal];
        // Ningún ancestro pactó este canal ⇒ no hay techo. Distinto de un 0% pactado, que sí topea.
        if (!superior || superior.aplica === false) continue;

        const terminosDelHijo = porCanalDelHijo[canal];
        const pctHijo = Number(terminosDelHijo?.porcentaje);
        if (!Number.isFinite(pctHijo)) continue;

        const baseHijo = terminosDelHijo?.base || BASE_GGR;
        const baseSuperior = superior.base || BASE_GGR;
        const techoSuperior = Number(superior.porcentaje);
        const comun = { canal, pctHijo, techoSuperior, baseHijo, baseSuperior };

        // Regla 1 — techo, sólo entre bases iguales (misma vara).
        if (baseHijo === baseSuperior && Number.isFinite(techoSuperior) && pctHijo > techoSuperior) {
            return { motivo: 'supera_techo', ...comun };
        }

        // Regla 2 — zona contigua: hijo ggr bajo superior recarga_jugada deja una zona salteada.
        if (baseHijo === BASE_GGR && baseSuperior === BASE_RECARGA_JUGADA) {
            return { motivo: 'zona_salteada', ...comun };
        }
    }
    return null;
}
